import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { MagnifyingGlass, Sparkle, BellSimpleRinging } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Self-service dialog for investors to manage the industries they want alerts for.
 * Reads from GET /api/me/interests, saves via PUT /api/me/interests.
 * Industry options are pulled from the admin-curated /api/companies/filters/options
 * so any change made in /admin/filters flows through automatically.
 */
export default function MyInterestsDialog({ open, onOpenChange }) {
  const [allIndustries, setAllIndustries] = useState([]);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setLoading(true);
    Promise.all([
      api.get("/companies/filters/options").catch(() => ({ data: {} })),
      api.get("/me/interests").catch(() => ({ data: { interested_industries: [] } })),
    ]).then(([opts, mine]) => {
      setAllIndustries(Array.isArray(opts?.data?.industries) ? opts.data.industries : []);
      setSelected(Array.isArray(mine?.data?.interested_industries) ? [...mine.data.interested_industries] : []);
    }).finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return allIndustries;
    return allIndustries.filter((i) => i.toLowerCase().includes(needle));
  }, [allIndustries, q]);

  const toggle = (industry) => {
    setSelected((cur) => cur.includes(industry) ? cur.filter((x) => x !== industry) : [...cur, industry]);
  };

  const selectAll = () => setSelected([...allIndustries]);
  const clearAll = () => setSelected([]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/me/interests", { industries: selected });
      toast.success("Interests updated");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save interests");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="my-interests-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold flex items-center gap-2">
            <BellSimpleRinging size={20} weight="fill" className="text-emerald-600" /> My industry alerts
          </DialogTitle>
          <DialogDescription>
            Pick the industries you want to be alerted about. You&apos;ll get an email + in-app notification each time Inventya Capital publishes a new company in any selected industry. You can update this at any time.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-3">
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search industries…"
                className="pl-9 h-10"
                data-testid="my-interests-search-input"
              />
            </div>

            <div className="flex items-center justify-between text-xs">
              <div className="text-muted-foreground">
                {selected.length} of {allIndustries.length} selected
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={selectAll} className="text-emerald-700 dark:text-emerald-400 font-semibold hover:underline" data-testid="my-interests-select-all">Select all</button>
                <span className="text-border">·</span>
                <button type="button" onClick={clearAll} className="text-muted-foreground hover:underline font-semibold" data-testid="my-interests-clear-all">Clear</button>
              </div>
            </div>

            {allIndustries.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                No industries are set up yet. Please check back later or contact your Inventya Capital relationship manager.
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto rounded-xl border border-border divide-y divide-border">
                {filtered.map((ind) => {
                  const isOn = selected.includes(ind);
                  return (
                    <label
                      key={ind}
                      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${isOn ? "bg-emerald-50 dark:bg-emerald-950/30" : "hover:bg-secondary"}`}
                      data-testid={`my-interest-row-${ind}`}
                    >
                      <Checkbox
                        checked={isOn}
                        onCheckedChange={() => toggle(ind)}
                        data-testid={`my-interest-checkbox-${ind}`}
                      />
                      <span className="text-sm font-medium flex-1">{ind}</span>
                      {isOn && <Sparkle size={13} weight="fill" className="text-emerald-600" />}
                    </label>
                  );
                })}
                {filtered.length === 0 && (
                  <div className="px-3 py-6 text-center text-xs text-muted-foreground">No industries match &quot;{q}&quot;</div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="my-interests-cancel-btn">Cancel</Button>
          <Button
            onClick={save}
            disabled={busy || loading}
            className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
            data-testid="my-interests-save-btn"
          >
            {busy ? "Saving…" : "Save interests"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
